<template>
    <div id="agent-authorization-page">
        <loader v-if="loading && !requestDetails && !errorMessage" :addBackground="true"></loader>

        <div class="container-fluid">
            <div class="box padding-large page-content">
                <div class="consent-wrapper">
                    <div v-if="isSuccess" class="consent-panel box box-elevated padding-large">
                        <div class="consent-header">
                            <img width="48" height="48" src="images/kevi.svg" :alt="t('login-logo_alt')" />
                            <h1>{{ t('agentAuthorizations-consent_success_title') }}</h1>
                            <p class="text-sm text-muted mb-0">
                                {{ t('agentAuthorizations-consent_success_subtitle') }}
                            </p>
                        </div>
                    </div>

                    <div v-else-if="requestDetails" class="consent-panel box box-elevated padding-large">
                        <div class="consent-header">
                            <img width="48" height="48" src="images/kevi.svg" :alt="t('login-logo_alt')" />
                            <h1>{{ t('agentAuthorizations-consent_title') }}</h1>
                            <p
                                class="text-sm text-muted mb-0"
                                v-html="t('agentAuthorizations-consent_logged_in_as', [ user.name || user.email ])"
                            ></p>
                        </div>

                        <div class="box box-secondary p-3 consent-details">
                            <p
                                class="mb-2"
                                v-html="t('agentAuthorizations-consent_question', [ formattedAddress, requestDetails.place.name ])"
                            ></p>
                        </div>

                        <button
                            class="btn btn-success btn-full"
                            type="button"
                            @click="confirmAuthorization"
                            :disabled="loading"
                        >
                            <span class="fa fa-spin fa-spinner" v-if="loading" aria-hidden="true"></span>
                            <span v-else>{{ t('agentAuthorizations-consent_confirm') }}</span>
                        </button>

                        <span class="error" v-if="errorMessage" role="alert" aria-live="assertive">
                            {{ errorMessage }}
                        </span>
                    </div>

                    <div v-else class="consent-panel box box-elevated padding-large">
                        <div class="consent-header">
                            <img width="48" height="48" src="images/kevi.svg" :alt="t('login-logo_alt')" />
                            <h1>{{ t('agentAuthorizations-consent_invalid_title') }}</h1>
                            <p class="text-sm text-muted mb-0">
                                {{ errorMessage || t('agentAuthorizations-consent_invalid_subtitle') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.page-content {
    min-height: calc(100vh - 20px);
    display: flex;
    flex-direction: column;
}

.consent-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    width: 100%;
}

.consent-panel {
    width: 100%;
    max-width: 480px;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.consent-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;

    h1 {
        margin-bottom: 0;
    }
}

.consent-details {
    :deep(strong) {
        color: #1f2937;
        font-weight: 600;
    }
}

.error[role="alert"] {
    color: #e74c3c;
    font-size: 0.9em;
    text-align: center;
}
</style>

<script>
import keviApi from "../keviApi";
import store from "../store";

export default {
    name: "AgentAuthorization",
    data() {
        return {
            loading: false,
            isSuccess: false,
            errorMessage: "",
            requestDetails: null,
            requestToken: null,
        };
    },
    computed: {
        user() {
            return store.state.user;
        },
        formattedAddress() {
            if (!this.requestDetails) {
                return "";
            }
            if (this.requestDetails.address_type === "whatsapp") {
                return this.requestDetails.address;
            }
            return this.requestDetails.address_type + "/" + this.requestDetails.address;
        },
    },
    mounted() {
        document.getElementsByTagName("title")[0].innerText = this.t("agentAuthorizations-consent_page_title");
        this.requestToken = this.$route.query.request || null;
        if (!this.requestToken) {
            this.errorMessage = this.t("agentAuthorizations-consent_missing_request");
            return;
        }
        this.loadRequest();
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        loadRequest() {
            this.loading = true;
            const options = keviApi.getOptions(
                "agent/authorization-requests/" + encodeURIComponent(this.requestToken),
                "GET"
            );
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    if (response.status === 200 && response.body.status === "success") {
                        this.requestDetails = response.body;
                        this.errorMessage = "";
                    } else {
                        this.errorMessage = this.t("agentAuthorizations-consent_invalid_subtitle");
                    }
                },
                () => {
                    this.loading = false;
                    this.errorMessage = this.t("agentAuthorizations-consent_invalid_subtitle");
                }
            );
        },
        confirmAuthorization() {
            this.loading = true;
            this.errorMessage = "";
            const options = keviApi.getOptions(
                "agent/authorization-requests/" + encodeURIComponent(this.requestToken) + "/confirm",
                "POST"
            );
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    if (response.status === 200 && response.body.status === "success") {
                        this.isSuccess = true;
                    } else {
                        this.errorMessage = this.t("agentAuthorizations-consent_confirm_error");
                    }
                },
                () => {
                    this.loading = false;
                    this.errorMessage = this.t("agentAuthorizations-consent_confirm_error");
                }
            );
        },
    },
};
</script>
